// The scenario list: "a sentence about the family, the moment you would be dropped into,
// and the seats you can take" (the plan this page implements). Reached from the plans
// list through a quiet link, never a banner — this is where a person with nothing to do
// goes looking, not something pushed in front of everyone. This screen creates nothing:
// each seat is a link to `CharacterPreview.tsx` (`/demo/:scenarioId/:seatLabel`), which
// shows that character's brief and is where starting the demo actually happens.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { DemoScenario } from '../api/contract.js';
import { session } from '../session.js';

export type DemoScenariosProps = {
  client: ApiClient;
};

export function DemoScenarios({ client }: DemoScenariosProps): JSX.Element {
  const [scenarios, setScenarios] = useState<readonly DemoScenario[] | null>(null);
  // A retryable failure: unreachable, a bad response, anything not named below. Never
  // set alongside `scenarios` — a failure leaves `scenarios` at `null`, so the "no demo"
  // paragraph below (which only renders once a real, successful empty answer has come
  // back) can never be reached by a request that was never actually answered.
  const [problem, setProblem] = useState<string | null>(null);
  // Demo mode is off. A 404 on this exact route is never "unknown address" the way it is
  // elsewhere (`@mor/api`'s `routes.ts` registers it unconditionally) — it is always this,
  // and it is a refusal, not an empty catalog, so it gets its own sentence.
  const [unavailable, setUnavailable] = useState(false);
  // A stale bearer, or — the moment right after a fresh page load — no bearer yet. Same
  // split `plan/usePlan.ts` and `Plans.tsx` already make: the vendor's next token publish
  // cures either one, so the screen says so rather than claiming there is nothing to offer.
  const [waiting, setWaiting] = useState(false);
  // A slow first read must not overwrite a fast retry: last started wins. Needed here for
  // the same reason `Plans.tsx` carries one — the retry effect below can call `load` again
  // while an earlier call is still in flight.
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = (generation.current += 1);
    try {
      const listed = await client.listDemoScenarios();
      if (mine !== generation.current) {
        return;
      }
      setScenarios(listed);
      setProblem(null);
      setUnavailable(false);
      setWaiting(false);
    } catch (error) {
      if (mine !== generation.current) {
        return;
      }
      if (error instanceof ApiFailure && error.code === 'unreachable') {
        setProblem('Couldn’t reach the server. Try again in a moment.');
        setUnavailable(false);
        setWaiting(false);
      } else if (error instanceof ApiFailure && error.status === 401) {
        setProblem(null);
        setUnavailable(false);
        setWaiting(true);
      } else if (error instanceof ApiFailure && error.status === 404) {
        setProblem(null);
        setUnavailable(true);
        setWaiting(false);
      } else {
        // Everything else: a 500, a response that would not decode, anything unnamed
        // above. Never reads as "no demo" — that would be a false reassurance about a
        // request that failed rather than one that was actually answered.
        setProblem('That didn’t load. Try again.');
        setUnavailable(false);
        setWaiting(false);
      }
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  // A fresh bearer and a returning network re-read the list — but only while it has never
  // rendered, so the vendor's 50-second token republish does not become a refetch
  // schedule. This is what turns a cold page load's first, tokenless request (a 401) into
  // scenarios actually appearing a moment later, with nobody reloading.
  useEffect(() => {
    const retry = (): void => {
      if (scenarios === null) {
        void load();
      }
    };
    const unsubscribe = session.subscribe(retry);
    window.addEventListener('online', retry);
    return () => {
      unsubscribe();
      window.removeEventListener('online', retry);
    };
  }, [load, scenarios]);

  return (
    <main className="screen screen--demo">
      <h1 className="screen__title">Try a demo</h1>
      {waiting ? (
        <p className="screen__loading" role="status">
          Signing you back in…
        </p>
      ) : null}
      {problem === null ? null : (
        <p className="screen__problem" role="status">
          {problem}
        </p>
      )}
      {problem === null ? null : (
        <button
          type="button"
          className="button button--outline"
          onClick={() => void load()}
        >
          Try again
        </button>
      )}
      {unavailable ? (
        <p className="screen__problem" role="status">
          Trying a demo isn’t available right now.
        </p>
      ) : null}
      {scenarios !== null && scenarios.length > 0 ? (
        <ul className="demoScenarios">
          {scenarios.map((scenario) => (
            <li key={scenario.id} className="demoScenario">
              <h2 className="demoScenario__name">{scenario.name}</h2>
              <p className="demoScenario__sentence">{scenario.sentence}</p>
              <p className="demoScenario__moment">{scenario.moment}</p>
              <p className="demoScenario__pick">Play as</p>
              <ul className="demoScenario__seats">
                {scenario.seats
                  .filter((seat) => seat.playable)
                  .map((seat) => {
                    // The picker's own label for who this seat is — falls back to the
                    // roster name alone when the scenario's author has not written one yet,
                    // so an unfinished scenario still lists every seat.
                    const seatLabel =
                      seat.introduction.length > 0 ? seat.introduction : seat.name;
                    return (
                      <li key={seat.label} className="demoScenario__seat">
                        <Link
                          className="button button--primary"
                          to={`/demo/${encodeURIComponent(scenario.id)}/${encodeURIComponent(seat.label)}`}
                        >
                          {seatLabel}
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            </li>
          ))}
        </ul>
      ) : null}
      {scenarios !== null && scenarios.length === 0 ? (
        <p className="screen__empty">There’s no demo to try right now.</p>
      ) : null}
    </main>
  );
}
